<script setup>
import { DEV_MODE } from '@/config/dev-mode';
import { findTermsAPI } from '@/api/fortuneApi';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';

const analysisStore = useAnalysisStore();
const analysisDataStore = useAnalysisDataStore();

const selectedItem = ref(null);
const selectedWord = ref('');
const allTermsData = ref(null);

/** 서비스별 용어 목록 분기 */
const dictionaryDataArray = computed(() => {
  const menu = analysisStore.selectedMenu || '';

  // 신년운세: yearlyFortuneData['8_행운용어'] (배열)
  if (menu.startsWith('/yearly-fortune')) {
    const terms = analysisDataStore.yearlyFortuneData?.['8_행운용어'];
    return Array.isArray(terms) ? terms : [];
  }
  // 용신분석: yData.용어해석 (쉼표 구분 문자열)
  if (menu.startsWith('/youngsin')) {
    return parseCommaString(analysisDataStore.yData?.용어해석);
  }
  // 낭만월운: TODO - 데이터 소스 확정 후 적용
  if (menu.startsWith('/monthly-fortune')) {
    return parseCommaString(analysisDataStore.pData?.용어해석);
  }
  // 사주풀이: sData.용어해석 (쉼표 구분 문자열)
  if (menu.startsWith('/summary')) {
    return parseCommaString(analysisDataStore.sData?.['용어해석']);
  }
  // 성향분석 (기본): pData.용어해석 (쉼표 구분 문자열)
  return parseCommaString(analysisDataStore.pData?.용어해석);
});

function parseCommaString(raw) {
  if (typeof raw === 'string' && raw) {
    return raw.split(',').map((item) => item.trim());
  }
  return [];
}

/** 용어 목록 변경 시 일괄 조회 */
watch(
  dictionaryDataArray,
  async (terms) => {
    selectedItem.value = null;
    selectedWord.value = '';
    allTermsData.value = null;

    if (terms.length > 0) {
      const params = {
        keywordCategory: '용어사전',
        keyword: terms.join(','),
      };
      const result = await findTermsAPI(params);
      if (result) {
        allTermsData.value = result.data;
        // 첫 번째 용어 자동 선택
        selectedItem.value = terms[0];
        selectedWord.value = terms[0];
        DEV_MODE && console.log('용어 일괄 조회 완료:', Object.keys(result.data));
      }
    }
  },
  { immediate: true },
);

/** 선택된 단어의 데이터 */
const currentWordData = computed(() => {
  if (!allTermsData.value || !selectedWord.value) return null;
  return allTermsData.value[selectedWord.value] || null;
});

/** 용어 클릭 (이미 조회된 데이터에서 선택) */
const clickDictionaryItem = (item) => {
  selectedItem.value = item;
  selectedWord.value = item;
};
</script>

<template>
  <div class="dictionary-wrap">
    <div
      v-for="(item, index) in dictionaryDataArray"
      :key="index"
      class="dictionary-item"
      :class="{ active: selectedItem === item }"
      @click="clickDictionaryItem(item)"
    >
      # {{ item }}
    </div>
  </div>
  <div class="dictionary-detail" v-if="selectedItem">
    <div class="category-card gray dictionary-desc__container">
      <div class="dictionary-desc__title">
        {{ selectedWord }}
        <div class="dictionary-desc__text">{{ currentWordData?.기준정보?.특징요약 }}</div>
      </div>

      <div class="divider"></div>

      <div v-if="currentWordData?.개념특징" class="dictionary-desc__content">
        <div class="dictionary-desc__label">개념</div>
        <div class="dictionary-desc__text">{{ currentWordData?.개념특징?.개념 }}</div>
      </div>

      <div v-if="currentWordData?.개념특징" class="dictionary-desc__content">
        <div class="dictionary-desc__label">특징</div>
        <div class="dictionary-desc__text">{{ currentWordData?.개념특징?.특징 }}</div>
      </div>

      <div v-if="currentWordData?.개념특징" class="dictionary-summary__container">
        <div class="category-card blue category-card__desc dictionary-summary__content">
          <div class="dictionary-desc__label">강점 요약</div>
          <div class="category-card__list">
            <div
              v-for="(item, index) in currentWordData.개념특징?.강점요약
                .split(',')
                .map((word) => word.trim())"
              :key="index"
              class="category-card__keyword blue"
            >
              {{ item }}
            </div>
          </div>
        </div>

        <div class="category-card red category-card__desc dictionary-summary__content">
          <div class="dictionary-desc__label">약점 요약</div>
          <div class="category-card__list">
            <div
              v-for="(item, index) in currentWordData.개념특징?.약점요약
                .split(',')
                .map((word) => word.trim())"
              :key="index"
              class="category-card__keyword red"
            >
              {{ item }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="gap-large"></div>
</template>

<style lang="scss" scoped>
.dictionary-wrap {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  align-self: stretch;
  flex-wrap: wrap;
  .dictionary-item {
    display: flex;
    padding: 6px 12px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 16px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    color: var(--Gray-600, #757575);
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px; /* 128.571% */
    cursor: pointer;
    &.active {
      background: var(--orange-300-main, #eb4c10);
      color: var(--gray-100-white, #fff);
    }
  }
  margin-bottom: 16px;
}

.dictionary-desc__container {
  width: 100%;
  min-height: 300px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border-radius: 8px;
  background: var(--Gray-200, #f8f8f8);
  overflow-y: auto;

  .dictionary-desc__title {
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: var(--Gray-800, #333);
    font-family: Pretendard;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 26px;

    .dictionary-desc__text {
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 22px;
    }
  }

  .divider {
    width: 100%;
    height: 1px;
    background: var(--Gray-300, #ebebeb);
  }

  .dictionary-desc__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .dictionary-desc__label {
    color: var(--Gray-800, #333);
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px; /* 157.143% */
  }

  .dictionary-desc__text {
    color: var(--Gray-800, #333);
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px; /* 157.143% */
  }
}

.dictionary-summary__container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .dictionary-desc__title {
    gap: 4px;
  }

  .dictionary-summary__content {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 8px;
    border-radius: 8px;

    .category-card__list {
      flex: 1;
      justify-content: flex-end;
      width: auto;
    }
  }
}

@media (max-width: 500px) {
  .dictionary-drawer__wrapper {
    width: 100%;
    min-width: 100%;
    left: 0;
    bottom: 0;
    overflow-y: auto;

    .dictionary-drawer__overlay {
      opacity: 0.4;
      background: var(--gray-900-black, #2c2c2e);
    }

    .dictionary-drawer__container {
      height: calc(100% - 52px);
      border-radius: 20px 20px 0px 0px;
      padding: 16px;

      .dictionary-drawer__header {
        margin-bottom: -8px;
        height: 52px;
        flex-direction: row;
        justify-content: center;
        align-items: center;

        .close-drawer {
          position: absolute;
          top: 14px;
          left: 0;
          width: 24px;
          height: 24px;
          background: url('/src/assets/images_manse/icon_close_1.svg') no-repeat center center;
          background-size: cover;
        }
      }

      .dictionary-desc__container {
        padding: 16px;

        .dictionary-desc__title {
          gap: 4px;
        }
      }
    }

    .dictionary-drawer__float-button {
      display: inline-flex;
    }

    .dictionary-drawer__top-fixed-button {
      display: none;
    }
  }
}
.category-card.blue {
  padding: 12px;
  background: rgba(87, 144, 252, 0.1);
}
.category-card.red {
  padding: 12px;
  background: rgba(255, 110, 110, 0.1);
}
.category-card__keyword {
  display: flex;
  padding: 3px 6px;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  border-radius: 4px;
  background: rgba(87, 87, 87, 0.1);
  color: var(--Gray-700, #575757);
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
}
.category-card__keyword.blue {
  background: rgba(87, 144, 252, 0.1);
  color: #5790fc;
}
.category-card__keyword.red {
  background: rgba(255, 110, 110, 0.1);
  color: #ff6e6e;
}
.category-card__list {
  display: flex;
  width: 100%;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
